if (!customElements.get('product-description-accordion')) {
  customElements.define(
    'product-description-accordion',
    class ProductDescriptionAccordion extends HTMLElement {
      connectedCallback() {
        if (this.initialized || this.initializationQueued) return;

        this.initializationQueued = true;
        queueMicrotask(() => {
          this.initializationQueued = false;
          if (this.isConnected && !this.initialized) this.enhance();
        });
      }

      enhance() {
        const fallback = Array.from(this.children).find((node) =>
          node.matches('details.product-description-accordion__item')
        );
        const source = fallback && Array.from(fallback.children).find((node) =>
          node.hasAttribute('data-description-source')
        );
        if (!source) return;

        this.initialized = true;
        const defaultHeading = this.dataset.defaultHeading ||
          fallback.querySelector('.product-description-accordion__label')?.textContent.trim();
        if (!defaultHeading) return;

        // Work on detached copies. Unrecognized or nested descriptions retain the
        // original, functional native disclosure, including all their markup.
        const copy = source.cloneNode(true);
        const groups = [];
        let current = this.createGroup(defaultHeading);
        let foundHeading = false;

        Array.from(copy.childNodes).forEach((node) => {
          const heading = this.getHeading(node);
          if (heading) {
            foundHeading = true;
            if (current.hasBody) {
              groups.push(current);
              current = this.createGroup(defaultHeading);
            }
            if (!current.heading) {
              current.heading = heading;
              current.label = heading.label;
            }
          }

          current.nodes.push(node);
          if (heading ? heading.hasBody : this.hasContent(node)) current.hasBody = true;
        });

        // Consecutive headings stay together. A trailing heading without a body
        // stays at the end of the previous panel instead of becoming an empty row.
        if (current.hasBody) {
          groups.push(current);
        } else if (groups.length) {
          groups[groups.length - 1].nodes.push(...current.nodes);
        }
        if (!foundHeading || !groups.length) return;

        const preservedNodes = groups.flatMap((group) => group.nodes);
        if (
          preservedNodes.length !== source.childNodes.length ||
          preservedNodes.map((node) => node.nodeType === Node.COMMENT_NODE ? '' : node.textContent).join('') !== source.textContent
        ) return;

        const fragment = document.createDocumentFragment();
        groups.forEach((group, index) => {
          const details = document.createElement('details');
          details.className = 'product-description-accordion__item';
          details.open = index === 0;

          const summary = document.createElement('summary');
          summary.className = 'product-description-accordion__summary';
          const label = document.createElement('span');
          label.className = 'product-description-accordion__label';
          label.textContent = group.label;
          const icon = document.createElement('span');
          icon.className = 'product-description-accordion__icon';
          icon.setAttribute('aria-hidden', 'true');
          summary.append(label, icon);

          const content = source.cloneNode(false);
          content.removeAttribute('data-description-source');
          // Retain heading text in the original content, while avoiding a visual
          // duplicate only when hiding it cannot hide links or rich content.
          if (group.heading?.hideNode) group.heading.hideNode.hidden = true;
          content.append(...group.nodes);
          details.append(summary, content);
          fragment.append(details);
        });

        fallback.replaceWith(fragment);
        this.dataset.enhanced = 'true';
      }

      createGroup(label) {
        return { label, heading: null, nodes: [], hasBody: false };
      }

      getHeading(node) {
        if (node.nodeType !== Node.ELEMENT_NODE || node.hasAttribute('hidden')) return null;

        if (/^H[1-6]$/.test(node.tagName)) {
          const label = this.headingText(node);
          return label ? {
            label,
            hasBody: false,
            hideNode: this.isPlainText(node) ? node : null,
          } : null;
        }
        if (node.tagName !== 'P') return null;

        const children = Array.from(node.childNodes);
        const first = children.find((child) => !this.isWhitespace(child));
        if (!first || first.nodeType !== Node.ELEMENT_NODE || !/^(STRONG|B)$/.test(first.tagName)) {
          return null;
        }

        const label = this.headingText(first);
        if (!label || label.length > 100) return null;
        const standalone = children.every((child) => child === first || this.isWhitespace(child));
        const shortHeading = label.length <= 80 && label.split(/\s+/).length <= 12 && !/[.!?]/.test(label);
        const knownLabel = /^(?:description|product description|product details|details|fabric|fabric details|fabric composition|composition|specifications|fabric specifications|features|key features|care|fabric care|care instructions|washing instructions|wash care|delivery|delivery information|delivery details|shipping|shipping information|shipping details|shipping policy|delivery\s*(?:&|and)\s*payment|shipping\s*(?:&|and)\s*delivery|delivery\s*(?:&|and)\s*shipping|shipping\s*(?:&|and)\s*returns|delivery\s*(?:&|and)\s*returns|returns|return policy|returns policy|returns\s*(?:&|and)\s*exchanges|return\s*(?:&|and)\s*exchange|exchange\s*(?:&|and)\s*returns|size\s*(?:&|and)\s*fit|fit\s*(?:&|and)\s*sizing|sizing|size guide|measurements)$/i.test(label);
        if (!(standalone && shortHeading) && !knownLabel) return null;

        return {
          label,
          hasBody: children.some((child) => child !== first && this.hasContent(child)),
          hideNode: this.isPlainText(first) ? (standalone ? node : first) : null,
        };
      }

      headingText(node) {
        return node.textContent.replace(/\s+/g, ' ').trim().replace(/\s*[:\uFF1A]\s*$/, '');
      }

      isWhitespace(node) {
        return node.nodeType === Node.COMMENT_NODE ||
          (node.nodeType === Node.TEXT_NODE && !node.textContent.trim());
      }

      isPlainText(node) {
        return Array.from(node.childNodes).every((child) =>
          child.nodeType === Node.TEXT_NODE || child.nodeType === Node.COMMENT_NODE
        );
      }

      hasContent(node) {
        if (node.nodeType === Node.TEXT_NODE) return !!node.textContent.trim();
        if (node.nodeType !== Node.ELEMENT_NODE) return false;
        if (node.hasAttribute('hidden') || /^(SCRIPT|STYLE|TEMPLATE)$/.test(node.tagName)) return false;
        if (node.textContent.trim()) return true;
        return node.matches('img, picture, video, audio, iframe, object, embed, canvas, svg, table, hr, input, button, select, textarea') ||
          !!node.querySelector('img, picture, video, audio, iframe, object, embed, canvas, svg, table, hr, input, button, select, textarea');
      }
    }
  );
}
